/**
 * useShellExposureHazards - what an exposed target does to whoever attacks it.
 *
 * Two callbacks the attack hook calls, both resolved here against the
 * ACTOR's own projection - the attack hook does not know who is swinging:
 *
 * - `onTouched({ target, outcome, isMelee })` (v8.353.0): a melee attack
 *   touched an exposed target - Spined bites back, Grasping holds, a curse
 *   (v8.368.4) lands on whoever hit. Written to the actor, told ONCE, to the
 *   target's log (v8.361.2): every per-tab entry is also shared, so a copy in
 *   the attacker's log was a SECOND shared line.
 * - `onDread({ target, targetId, isMelee })` (v8.354.0): the gates (v8.368.2),
 *   Sanctioned and Taunt (v8.356.0), then a Terrifying target's Willpower
 *   test, remembered on the actor once per target unless the table tests
 *   every attack. Returns whether the attack may proceed.
 *
 * A legacy attack profile is not a combatant: the hazard is still resolved
 * and told - against a stand-in with a plain Toughness - but nobody's state
 * is written, and the line says "apply it by hand". Extracted from
 * DeathWatchSimulator; every log line is unchanged.
 */
import { useCallback } from 'react';
import { emptyTarget } from '../engine/factories';
import { decodeCond, mergeCondition } from '../engine/conditions';
import { projectDefender } from '../engine/roster/projection';
import { resolveHazards } from '../engine/exposureHazards';
import { resolveDread } from '../engine/exposureDread';
import { sanctionedAgainst, tauntsReader, attackGate } from '../engine/exposures';
import { cursesFromExposures } from '../engine/exposureAfflict';
import { infoLine } from './shellCombatant';

/** The exposure-bearing half of a target. */
function bearerOf(t) {
  return { conditions: t.conditions, exposures: t.exposures, teamTag: t.teamTag, sideTag: t.sideTag };
}

/** Who is reading the exposure: the actor's team and auras, in this mode. */
function readerOf(actorProjection, isMelee) {
  return {
    teamTag: actorProjection?.teamTag ?? null,
    sideTag: actorProjection?.sideTag ?? null,
    auras: actorProjection?.auras,
    isMelee,
  };
}

/** The hazard's outcome with any curse folded in; `notes` empty means nothing happened. */
export function touchOutcome(touched, outcome, isMelee, actorProjection) {
  const bearer = bearerOf(touched);
  const reader = readerOf(actorProjection, !!isMelee);
  const out = resolveHazards(bearer, actorProjection ?? emptyTarget(), reader, outcome);
  const curses = outcome === 'hit' ? cursesFromExposures(bearer, reader) : [];
  for (const { code, label } of curses) {
    out.conditions.push(code);
    out.notes.push('◎ ' + label + ': ' + (decodeCond(code) || code) + ' on whoever struck them.');
  }
  return out;
}

/** The hazard's write: wounds off, conditions refreshed on. */
function hazardWrite(out) {
  return (st) => {
    const conditions = [...(st.conditions ?? [])];
    for (const c of out.conditions) mergeCondition(conditions, c, 'refresh');
    return { ...st, woundsCurrent: (st.woundsCurrent ?? 0) - out.damage, conditions };
  };
}

/** A reminder per other combatant whose Taunt demands this attacker's attention. */
export function tauntLines(roster, targetId, reader) {
  const lines = [];
  for (const c of roster) {
    if (c.id === targetId) continue;
    const other = projectDefender(c);
    if (tauntsReader(bearerOf(other), reader)) {
      lines.push('🗣 Taunting: ' + c.name + ' demands to be faced - the GM may rule this attack goes to them.');
    }
  }
  return lines;
}

/** Refused before anything: untouchable, unshootable, out of reach, or Sanctioned. */
export function dreadRefusal(dreaded, bearer, reader, distanceM) {
  const gate = attackGate(bearer, { ...reader, distanceM });
  if (gate.refused) return '⛔ ' + gate.refused;
  if (sanctionedAgainst(bearer, reader)) {
    return '⛔ Sanctioned: ' + (dreaded.name || 'they') + ' may not be attacked by their own side.';
  }
  return null;
}

/** The dread test's write: the penalty condition, and the memo when it has one. */
function dreadWrite(out) {
  return (st) => {
    const conditions = [...(st.conditions ?? [])];
    if (out.penalty) mergeCondition(conditions, out.penalty, 'refresh');
    return { ...st, conditions, ...(Object.keys(out.memo).length ? { dreadTested: out.memo } : {}) };
  };
}

/** Apply a touch: to the actor if there is one, otherwise a note to do it by hand; then tell the target's log. */
function applyTouch(out, { actor, activeTab, updateCombatantState, addNarrative }) {
  if (!out.notes.length) return;
  if (actor) updateCombatantState(actor.id, hazardWrite(out));
  else out.notes.push('⚠ No acting combatant to bite - apply it by hand.');
  if (!activeTab) return;
  for (const text of out.notes) addNarrative(activeTab, infoLine(text));
}

/** Test dread (the gates already passed) and say so; returns whether the attack proceeds. */
/** The dread lines to write: the resolver's note, and a hand-apply note with nobody to shake. */
function dreadLines(out, actor) {
  return [out.note, !actor && out.penalty ? '😨 No acting combatant to shake - apply it by hand.' : null].filter(
    Boolean,
  );
}

/** A creature's Fear Rating for the kill-team's pool (D12): its sheet's, or 1 for a dread worn by hand. */
const fearRatingOf = (dreaded) => (dreaded?.fearRating > 0 ? dreaded.fearRating : 1);

/**
 * Squad Mode (D12): a failed test by the squad's (acting) leader costs its pool the creature's Fear Rating - once per
 * creature, as the dread memo already keeps it. The kill-team decides whether this actor leads.
 */
function tellFearFailed(out, actor, dreaded, deps) {
  if (!actor || !out.test || out.test.passed) return;
  deps.onFearFailed?.(actor.id, fearRatingOf(dreaded), dreaded?.name || 'them');
}

function testDread(targetId, bearer, reader, deps, dreaded) {
  const { actor, actorProjection } = deps;
  const memo = actor?.state?.dreadTested ?? {};
  const out = resolveDread(actorProjection ?? emptyTarget(), bearer, reader, targetId, memo, {
    perAttack: deps.dreadPerAttack || !actor,
  });
  if (!out.needed && !out.note) return true;
  if (actor && (out.needed || out.penalty)) deps.updateCombatantState(actor.id, dreadWrite(out));
  for (const text of dreadLines(out, actor)) deps.addNarrative(targetId, infoLine(text));
  tellFearFailed(out, actor, dreaded, deps);
  return out.proceed;
}

/**
 * The whole dread check. Each line is written ONCE, to the target's log
 * (v8.361.2); a Taunt is a reminder, never a rule.
 */
function checkDread({ target: dreaded, targetId, isMelee }, deps) {
  const bearer = bearerOf(dreaded);
  const reader = readerOf(deps.actorProjection, isMelee);
  const say = (text) => deps.addNarrative(targetId, infoLine(text));
  const refusal = dreadRefusal(dreaded, bearer, reader, deps.measurement?.metres ?? null);
  if (refusal) {
    say(refusal);
    return false;
  }
  for (const line of tauntLines(deps.roster, targetId, reader)) say(line);
  return testDread(targetId, bearer, reader, deps, dreaded);
}

/**
 * @param {object} args - actor, actorProjection, activeTab, roster,
 *   measurement (the live map's, for the reach gate), dreadPerAttack (the
 *   house rule), updateCombatantState, addNarrative, onFearFailed (Squad Mode: the actor failed a Fear test -
 *   `(actorId, fearRating, creature)`; the kill-team decides whether he leads, D12).
 * @returns {{ onTouched: Function, onDread: Function }}
 */
export default function useShellExposureHazards(args) {
  const { actor, actorProjection, activeTab, roster, measurement, dreadPerAttack } = args;
  const { updateCombatantState, addNarrative, onFearFailed } = args;
  const onTouched = useCallback(
    ({ target, outcome, isMelee }) =>
      applyTouch(touchOutcome(target, outcome, isMelee, actorProjection), {
        actor,
        activeTab,
        updateCombatantState,
        addNarrative,
      }),
    [actor, actorProjection, activeTab, updateCombatantState, addNarrative],
  );
  const onDread = useCallback(
    (attack) =>
      checkDread(attack, {
        actor,
        actorProjection,
        roster,
        measurement,
        dreadPerAttack,
        updateCombatantState,
        addNarrative,
        onFearFailed,
      }),
    [actor, actorProjection, dreadPerAttack, updateCombatantState, addNarrative, roster, measurement, onFearFailed],
  );
  return { onTouched, onDread };
}
